/**
 * Страница администрирования для SuperAdmin: управление тенантами, пользователями, ролями, аудит
 * Дата: 29.01.2026 - TODO-043: Итерация 4
 * Дата: 30.01.2026 - Рефакторинг под унифицированные UI компоненты (TODO-045)
 * 
 * СВЯЗАННЫЕ МОДУЛИ И ФАЙЛЫ:
 * - frontend/src/api/admin.ts - API для администрирования
 * - frontend/src/api/users.ts - API для работы с пользователями
 * - frontend/src/api/roles.ts - API для работы с ролями
 * - components/ui/StatusBadge.tsx - унифицированный статус
 * - components/ui/EmptyState.tsx - унифицированное пустое состояние
 * - components/ui/LoadingState.tsx - унифицированная загрузка
 * - components/ui/ActionButtons.tsx - унифицированные кнопки действий
 * 
 * КАК РАБОТАЕТ:
 * - Отображает вкладки: Тенанты, Пользователи, Роли, Аудит, Статистика
 * - Загружает данные для каждой вкладки при переключении
 * - Управление тенантами (деактивация)
 * - Просмотр всех пользователей системы
 * - Просмотр ролей и аудит-лога
 * 
 * СХЕМА:
 * AdminPage → adminApi → backend API
 * AdminPage → StatusBadge → отображение статусов
 * AdminPage → ActionButtons → действия с тенантами
 */
import { useState, useEffect } from 'react';
import { MainLayout } from '@/components/layout/MainLayout';
import { 
  Card, 
  CardHeader, 
  CardTitle, 
  CardContent, 
  Button, 
  Input,
  Select,
  Textarea,
  Modal,
  Table, 
  TableHead, 
  TableBody, 
  TableRow, 
  TableHeader, 
  TableCell,
  StatusBadge,
  EmptyState,
  LoadingState,
  ActionButtons
} from '@/components/ui';
import { adminApi, Tenant, SystemStats, AuditLogEntry, AllUsersItem, StaffIdentity, StaffIdentityRequest } from '@/api/admin';
import { rolesApi, Role } from '@/api/roles';
import { useToast } from '@/hooks/useToast';
import { useAuth } from '@/hooks/useAuth';
import { Plus, Power, Building2, Users, Shield, FileText } from 'lucide-react';
import { formatDateTime } from '@/utils/formatters';

export function AdminPage() {
  const { user } = useAuth();
  const { showSuccess, showError } = useToast();
  const [activeTab, setActiveTab] = useState('tenants');
  
  // Tenants
  const [tenants, setTenants] = useState<Tenant[]>([]);
  const [isLoadingTenants, setIsLoadingTenants] = useState(false);
  
  // Users (all tenants) — Дата: 30.01.2026
  const [users, setUsers] = useState<AllUsersItem[]>([]);
  const [isLoadingUsers, setIsLoadingUsers] = useState(false);
  const [usersError, setUsersError] = useState<string | null>(null);
  
  // Roles
  const [roles, setRoles] = useState<Role[]>([]);
  const [isLoadingRoles, setIsLoadingRoles] = useState(false);
  
  // Stats
  const [stats, setStats] = useState<SystemStats | null>(null);
  
  // Audit
  const [auditLog, setAuditLog] = useState<AuditLogEntry[]>([]);
  const [isLoadingAudit, setIsLoadingAudit] = useState(false);

  // Staff identities
  const [staffIdentities, setStaffIdentities] = useState<StaffIdentity[]>([]);
  const [isLoadingStaffIdentities, setIsLoadingStaffIdentities] = useState(false);
  const [isStaffModalOpen, setIsStaffModalOpen] = useState(false);
  const [editingStaffIdentity, setEditingStaffIdentity] = useState<StaffIdentity | null>(null);
  const [staffSearch, setStaffSearch] = useState('');
  const [staffTenantFilter, setStaffTenantFilter] = useState('');
  const [staffStatusFilter, setStaffStatusFilter] = useState<'all' | 'active' | 'inactive'>('active');
  const [staffForm, setStaffForm] = useState<StaffIdentityRequest>({
    tenant_id: '',
    label: '',
    comment: '',
    phone: '',
    telegram_id: '',
    max_id: '',
    is_active: true,
  });

  // Проверка SuperAdmin
  if (user?.tenant_id) {
    return (
      <MainLayout>
        <div className="p-6">
          <Card>
            <CardContent className="p-6 text-center">
              <p className="text-red-600">Доступ запрещён. Только для SuperAdmin.</p>
            </CardContent>
          </Card>
        </div>
      </MainLayout>
    );
  }

  // Загрузка данных
  useEffect(() => {
    if (activeTab === 'tenants') {
      loadTenants();
    } else if (activeTab === 'users') {
      loadUsers();
    } else if (activeTab === 'staff-identities') {
      if (tenants.length === 0) {
        loadTenants();
      }
      loadStaffIdentities();
    } else if (activeTab === 'roles') {
      loadRoles();
    } else if (activeTab === 'audit') {
      loadAuditLog();
    } else if (activeTab === 'stats') {
      loadStats();
    }
  }, [activeTab, staffSearch, staffTenantFilter, staffStatusFilter]);

  const loadTenants = async () => {
    setIsLoadingTenants(true);
    try {
      const data = await adminApi.listTenants();
      setTenants(data.tenants);
    } catch (error: any) {
      showError('Не удалось загрузить тенанты');
    } finally {
      setIsLoadingTenants(false);
    }
  };

  const loadUsers = async () => {
    setIsLoadingUsers(true);
    setUsersError(null);
    try {
      // Дата: 30.01.2026 — SuperAdmin использует /admin/users для получения ВСЕХ пользователей
      const data = await adminApi.listAllUsers({ limit: 500 });
      setUsers(data.users);
    } catch (error: any) {
      const message = error?.response?.data?.error || 'Не удалось загрузить пользователей';
      setUsersError(message);
      showError(message);
    } finally {
      setIsLoadingUsers(false);
    }
  };

  const loadRoles = async () => {
    setIsLoadingRoles(true);
    try {
      const data = await rolesApi.list();
      setRoles(data);
    } catch (error: any) {
      showError('Не удалось загрузить роли');
    } finally {
      setIsLoadingRoles(false);
    }
  };

  const loadStats = async () => {
    try {
      const data = await adminApi.getStats();
      setStats(data);
    } catch (error: any) {
      showError('Не удалось загрузить статистику');
    }
  };

  const loadAuditLog = async () => {
    setIsLoadingAudit(true);
    try {
      const data = await adminApi.getAuditLog({ limit: 100 });
      setAuditLog(data.entries);
    } catch (error: any) {
      showError('Не удалось загрузить аудит-лог');
    } finally {
      setIsLoadingAudit(false);
    }
  };

  const loadStaffIdentities = async () => {
    setIsLoadingStaffIdentities(true);
    try {
      const data = await adminApi.listStaffIdentities({
        limit: 200,
        search: staffSearch || undefined,
        tenant_id: staffTenantFilter || undefined,
        is_active: staffStatusFilter === 'all' ? undefined : staffStatusFilter === 'active',
      });
      setStaffIdentities(data.items);
    } catch (error: any) {
      showError('Не удалось загрузить staff/internal registry');
    } finally {
      setIsLoadingStaffIdentities(false);
    }
  };

  const resetStaffForm = () => {
    setEditingStaffIdentity(null);
    setStaffForm({
      tenant_id: staffTenantFilter || '',
      label: '',
      comment: '',
      phone: '',
      telegram_id: '',
      max_id: '',
      is_active: true,
    });
  };

  const openCreateStaffModal = () => {
    resetStaffForm();
    setIsStaffModalOpen(true);
  };

  const openEditStaffModal = (item: StaffIdentity) => {
    setEditingStaffIdentity(item);
    setStaffForm({
      tenant_id: item.tenant_id,
      label: item.label,
      comment: item.comment || '',
      phone: item.phone_normalized || '',
      telegram_id: item.telegram_id || '',
      max_id: item.max_id || '',
      is_active: item.is_active,
    });
    setIsStaffModalOpen(true);
  };

  const handleSubmitStaffIdentity = async () => {
    if (!staffForm.tenant_id || !staffForm.label.trim()) {
      showError('Укажите тенант и label');
      return;
    }
    try {
      if (editingStaffIdentity) {
        await adminApi.updateStaffIdentity(editingStaffIdentity.id, staffForm);
        showSuccess('Запись обновлена');
      } else {
        await adminApi.createStaffIdentity(staffForm);
        showSuccess('Запись добавлена');
      }
      setIsStaffModalOpen(false);
      resetStaffForm();
      loadStaffIdentities();
    } catch (error: any) {
      showError(error?.response?.data?.error || 'Не удалось сохранить запись');
    }
  };

  const handleToggleStaffIdentity = async (item: StaffIdentity) => {
    try {
      await adminApi.updateStaffIdentity(item.id, {
        tenant_id: item.tenant_id,
        label: item.label,
        comment: item.comment || '',
        phone: item.phone_normalized || '',
        telegram_id: item.telegram_id || '',
        max_id: item.max_id || '',
        is_active: !item.is_active,
      });
      showSuccess(item.is_active ? 'Запись деактивирована' : 'Запись активирована');
      loadStaffIdentities();
    } catch (error: any) {
      showError('Не удалось изменить статус записи');
    }
  };

  const handleDeactivateTenant = async (id: string) => {
    if (!confirm('Деактивировать тенант?')) return;
    try {
      await adminApi.deactivateTenant(id);
      showSuccess('Тенант деактивирован');
      loadTenants();
    } catch (error: any) {
      showError('Не удалось деактивировать тенант');
    }
  };

  return (
    <MainLayout headerTitle="Администрирование">
      <div className="p-6">
        {/* Вкладки */}
        <div className="flex gap-2 mb-6 border-b border-neutral-200">
          <button
            onClick={() => setActiveTab('tenants')}
            className={`px-4 py-2 font-medium text-sm transition-colors flex items-center gap-2 ${
              activeTab === 'tenants'
                ? 'text-primary-600 border-b-2 border-primary-600'
                : 'text-neutral-600 hover:text-neutral-900'
            }`}
          >
            <Building2 className="w-4 h-4" />
            Тенанты
          </button>
          <button
            onClick={() => setActiveTab('users')}
            className={`px-4 py-2 font-medium text-sm transition-colors flex items-center gap-2 ${
              activeTab === 'users'
                ? 'text-primary-600 border-b-2 border-primary-600'
                : 'text-neutral-600 hover:text-neutral-900'
            }`}
          >
            <Users className="w-4 h-4" />
            Пользователи
          </button>
          <button
            onClick={() => setActiveTab('staff-identities')}
            className={`px-4 py-2 font-medium text-sm transition-colors flex items-center gap-2 ${
              activeTab === 'staff-identities'
                ? 'text-primary-600 border-b-2 border-primary-600'
                : 'text-neutral-600 hover:text-neutral-900'
            }`}
          >
            <Users className="w-4 h-4" />
            Staff registry
          </button>
          <button
            onClick={() => setActiveTab('roles')}
            className={`px-4 py-2 font-medium text-sm transition-colors flex items-center gap-2 ${
              activeTab === 'roles'
                ? 'text-primary-600 border-b-2 border-primary-600'
                : 'text-neutral-600 hover:text-neutral-900'
            }`}
          >
            <Shield className="w-4 h-4" />
            Роли
          </button>
          <button
            onClick={() => setActiveTab('audit')}
            className={`px-4 py-2 font-medium text-sm transition-colors flex items-center gap-2 ${
              activeTab === 'audit'
                ? 'text-primary-600 border-b-2 border-primary-600'
                : 'text-neutral-600 hover:text-neutral-900'
            }`}
          >
            <FileText className="w-4 h-4" />
            Аудит
          </button>
          <button
            onClick={() => setActiveTab('stats')}
            className={`px-4 py-2 font-medium text-sm transition-colors flex items-center gap-2 ${
              activeTab === 'stats'
                ? 'text-primary-600 border-b-2 border-primary-600'
                : 'text-neutral-600 hover:text-neutral-900'
            }`}
          >
            <Power className="w-4 h-4" />
            Статистика
          </button>
        </div>

        {/* Tenants Tab */}
        {activeTab === 'tenants' && (
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle>Тенанты</CardTitle>
                  <Button>
                    <Plus className="w-4 h-4 mr-2" />
                    Создать тенант
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                {isLoadingTenants ? (
                  <LoadingState />
                ) : tenants.length === 0 ? (
                  <EmptyState 
                    type="noData"
                    title="Нет тенантов"
                    description="Создайте первый тенант"
                  />
                ) : (
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableHeader>Название</TableHeader>
                        <TableHeader>Slug</TableHeader>
                        <TableHeader>Статус</TableHeader>
                        <TableHeader>Создан</TableHeader>
                        <TableHeader>Действия</TableHeader>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {tenants.map((tenant) => (
                        <TableRow key={tenant.id}>
                          <TableCell>{tenant.name}</TableCell>
                          <TableCell>{tenant.slug}</TableCell>
                          <TableCell>
                            <StatusBadge status={tenant.is_active} />
                          </TableCell>
                          <TableCell>{formatDateTime(tenant.created_at)}</TableCell>
                          <TableCell>
                            <ActionButtons
                              onEdit={() => {/* TODO: открыть модалку редактирования */}}
                              onToggle={tenant.is_active ? () => handleDeactivateTenant(tenant.id) : undefined}
                              isActive={tenant.is_active}
                              testIdPrefix={`tenant-${tenant.id}`}
                            />
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </CardContent>
            </Card>
        )}

        {/* Users Tab */}
        {activeTab === 'users' && (
            <Card>
              <CardHeader>
                <CardTitle>Пользователи (все тенанты)</CardTitle>
              </CardHeader>
              <CardContent>
                {isLoadingUsers ? (
                  <LoadingState />
                ) : usersError && users.length === 0 ? (
                  <EmptyState 
                    type="noUsers"
                    title="Не удалось загрузить пользователей"
                    description={usersError}
                    actionText="Повторить"
                    onAction={loadUsers}
                  />
                ) : !users || users.length === 0 ? (
                  <EmptyState 
                    type="noUsers"
                    title="Нет пользователей"
                    description="Пользователи не найдены"
                  />
                ) : (
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableHeader>Имя</TableHeader>
                        <TableHeader>Email</TableHeader>
                        <TableHeader>Роль</TableHeader>
                        <TableHeader>Тенант</TableHeader>
                        <TableHeader>Статус</TableHeader>
                        <TableHeader>Создан</TableHeader>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {/* 29.01.2026 РЕВЬЮ: переименовано u → избежание shadowing с user из useAuth */}
                      {users.map((u) => (
                        <TableRow key={u.id}>
                          <TableCell>{u.full_name}</TableCell>
                          <TableCell>{u.email}</TableCell>
                          <TableCell>{u.role_name}</TableCell>
                          <TableCell>{u.tenant_id || 'SuperAdmin'}</TableCell>
                          <TableCell>
                            <StatusBadge status={u.is_active} />
                          </TableCell>
                          <TableCell>{formatDateTime(u.created_at)}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </CardContent>
            </Card>
        )}

        {activeTab === 'staff-identities' && (
          <div className="space-y-6">
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between gap-4">
                  <CardTitle>Staff / internal identities</CardTitle>
                  <Button onClick={openCreateStaffModal}>
                    <Plus className="w-4 h-4 mr-2" />
                    Добавить запись
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                  <Input
                    placeholder="Поиск по label / phone / tg / max"
                    value={staffSearch}
                    onChange={(e) => setStaffSearch(e.target.value)}
                  />
                  <Select
                    value={staffTenantFilter}
                    onChange={(e) => setStaffTenantFilter(e.target.value)}
                    options={[
                      { value: '', label: 'Все тенанты' },
                      ...tenants.map((tenant) => ({ value: tenant.id, label: tenant.name })),
                    ]}
                  />
                  <Select
                    value={staffStatusFilter}
                    onChange={(e) => setStaffStatusFilter(e.target.value as 'all' | 'active' | 'inactive')}
                    options={[
                      { value: 'all', label: 'Все статусы' },
                      { value: 'active', label: 'Только активные' },
                      { value: 'inactive', label: 'Только неактивные' },
                    ]}
                  />
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardContent>
                {isLoadingStaffIdentities ? (
                  <LoadingState />
                ) : staffIdentities.length === 0 ? (
                  <EmptyState
                    type="noData"
                    title="Registry пуст"
                    description="Добавьте сотрудников, internal TG/MAX IDs или служебные телефоны"
                  />
                ) : (
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableHeader>Label</TableHeader>
                        <TableHeader>Тенант</TableHeader>
                        <TableHeader>Телефон</TableHeader>
                        <TableHeader>Telegram</TableHeader>
                        <TableHeader>MAX</TableHeader>
                        <TableHeader>Статус</TableHeader>
                        <TableHeader>Обновлён</TableHeader>
                        <TableHeader>Действия</TableHeader>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {staffIdentities.map((item) => (
                        <TableRow key={item.id}>
                          <TableCell>
                            <div className="font-medium">{item.label}</div>
                            <div className="text-xs text-neutral-500">{item.comment || '-'}</div>
                          </TableCell>
                          <TableCell>{item.tenant_name || item.tenant_id}</TableCell>
                          <TableCell>{item.phone_normalized || '-'}</TableCell>
                          <TableCell>{item.telegram_id || '-'}</TableCell>
                          <TableCell>{item.max_id || '-'}</TableCell>
                          <TableCell>
                            <StatusBadge status={item.is_active} />
                          </TableCell>
                          <TableCell>{formatDateTime(item.updated_at)}</TableCell>
                          <TableCell>
                            <ActionButtons
                              onEdit={() => openEditStaffModal(item)}
                              onToggle={() => handleToggleStaffIdentity(item)}
                              isActive={item.is_active}
                              testIdPrefix={`staff-identity-${item.id}`}
                            />
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </CardContent>
            </Card>
          </div>
        )}

        {/* Roles Tab */}
        {activeTab === 'roles' && (
            <Card>
              <CardHeader>
                <CardTitle>Роли</CardTitle>
              </CardHeader>
              <CardContent>
                {isLoadingRoles ? (
                  <LoadingState />
                ) : roles.length === 0 ? (
                  <EmptyState 
                    type="noData"
                    title="Нет ролей"
                    description="Роли не найдены"
                  />
                ) : (
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableHeader>Название</TableHeader>
                        <TableHeader>Описание</TableHeader>
                        <TableHeader>Тенант</TableHeader>
                        <TableHeader>Создан</TableHeader>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {roles.map((role) => (
                        <TableRow key={role.id}>
                          <TableCell>{role.name}</TableCell>
                          <TableCell>{role.description || '-'}</TableCell>
                          <TableCell>{role.tenant_id || 'Глобальная'}</TableCell>
                          <TableCell>{formatDateTime(role.created_at)}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </CardContent>
            </Card>
        )}

        {/* Audit Tab */}
        {activeTab === 'audit' && (
            <Card>
              <CardHeader>
                <CardTitle>Аудит-лог</CardTitle>
              </CardHeader>
              <CardContent>
                {isLoadingAudit ? (
                  <LoadingState />
                ) : auditLog.length === 0 ? (
                  <EmptyState 
                    type="noData"
                    title="Нет записей аудита"
                    description="Аудит-лог пуст"
                  />
                ) : (
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableHeader>Дата</TableHeader>
                        <TableHeader>Пользователь</TableHeader>
                        <TableHeader>Действие</TableHeader>
                        <TableHeader>Ресурс</TableHeader>
                        <TableHeader>IP</TableHeader>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {auditLog.map((entry) => (
                        <TableRow key={entry.id}>
                          <TableCell>{formatDateTime(entry.created_at)}</TableCell>
                          <TableCell>{entry.username}</TableCell>
                          <TableCell>{entry.action}</TableCell>
                          <TableCell>{entry.resource_type}</TableCell>
                          <TableCell>{entry.ip_address}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </CardContent>
            </Card>
        )}

        {/* Stats Tab */}
        {activeTab === 'stats' && (
            <Card>
              <CardHeader>
                <CardTitle>Статистика системы</CardTitle>
              </CardHeader>
              <CardContent>
                {stats ? (
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <div className="p-4 bg-neutral-100 rounded-lg">
                      <p className="text-sm text-neutral-600">Всего тенантов</p>
                      <p className="text-2xl font-bold">{stats.total_tenants}</p>
                    </div>
                    <div className="p-4 bg-neutral-100 rounded-lg">
                      <p className="text-sm text-neutral-600">Активных тенантов</p>
                      <p className="text-2xl font-bold">{stats.active_tenants}</p>
                    </div>
                    <div className="p-4 bg-neutral-100 rounded-lg">
                      <p className="text-sm text-neutral-600">Всего пользователей</p>
                      <p className="text-2xl font-bold">{stats.total_users}</p>
                    </div>
                    <div className="p-4 bg-neutral-100 rounded-lg">
                      <p className="text-sm text-neutral-600">Всего сообщений</p>
                      <p className="text-2xl font-bold">{stats.total_messages}</p>
                    </div>
                  </div>
                ) : (
                  <LoadingState />
                )}
              </CardContent>
            </Card>
        )}

        <Modal
          isOpen={isStaffModalOpen}
          onClose={() => {
            setIsStaffModalOpen(false);
            resetStaffForm();
          }}
          title={editingStaffIdentity ? 'Редактировать staff identity' : 'Новая staff identity'}
          footer={
            <div className="flex justify-end gap-2">
              <Button variant="secondary" onClick={() => {
                setIsStaffModalOpen(false);
                resetStaffForm();
              }}>
                Отмена
              </Button>
              <Button onClick={handleSubmitStaffIdentity}>
                {editingStaffIdentity ? 'Сохранить' : 'Создать'}
              </Button>
            </div>
          }
        >
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium mb-2">Тенант</label>
              <Select
                value={staffForm.tenant_id}
                onChange={(e) => setStaffForm((prev) => ({ ...prev, tenant_id: e.target.value }))}
                options={[
                  { value: '', label: 'Выберите тенант' },
                  ...tenants.map((tenant) => ({ value: tenant.id, label: tenant.name })),
                ]}
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-2">Label</label>
              <Input
                value={staffForm.label}
                onChange={(e) => setStaffForm((prev) => ({ ...prev, label: e.target.value }))}
                placeholder="Например: Старшая медсестра / TG bot support"
              />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <div>
                <label className="block text-sm font-medium mb-2">Телефон</label>
                <Input
                  value={staffForm.phone || ''}
                  onChange={(e) => setStaffForm((prev) => ({ ...prev, phone: e.target.value }))}
                  placeholder="79001234567"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Telegram ID</label>
                <Input
                  value={staffForm.telegram_id || ''}
                  onChange={(e) => setStaffForm((prev) => ({ ...prev, telegram_id: e.target.value }))}
                  placeholder="123456789"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">MAX ID</label>
                <Input
                  value={staffForm.max_id || ''}
                  onChange={(e) => setStaffForm((prev) => ({ ...prev, max_id: e.target.value }))}
                  placeholder="max-user-id"
                />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium mb-2">Комментарий</label>
              <Textarea
                value={staffForm.comment || ''}
                onChange={(e) => setStaffForm((prev) => ({ ...prev, comment: e.target.value }))}
                placeholder="Зачем эта запись нужна QC/public API classifier"
                rows={3}
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-2">Статус</label>
              <Select
                value={staffForm.is_active ? 'active' : 'inactive'}
                onChange={(e) => setStaffForm((prev) => ({ ...prev, is_active: e.target.value === 'active' }))}
                options={[
                  { value: 'active', label: 'Активна' },
                  { value: 'inactive', label: 'Неактивна' },
                ]}
              />
            </div>
          </div>
        </Modal>
      </div>
    </MainLayout>
  );
}
